import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { FaChevronDown } from 'react-icons/fa';
import { useApiData } from '../hooks/useApiData';
import { fetchHeroSection, fetchImage } from '../repository/api';
import FullPageLoader from './FullPageLoader';
import APIintergration from '../assets/API-intergration-mobipesa.jpg';
import softwarehero from '../assets/software-hero.jpg';
import shortcode from '../assets/short-code.jpg';
import bulkSMS from '../assets/bulk-sms.jpg';
import payment from '../assets/payment-solutions.jpg';

// Default service content as fallback
const defaultServices = [
  {
    title: "API INTEGRATION",
    description: "Codewave offers a full range of consulting services in all the countries.",
    image: APIintergration,
    color: "#2090B0"
  },
  {
    title: "SHORT CODE",
    description: "Create interactive mobile experiences with our USSD solutions.",
    image: shortcode, 
    color: "#105090"
  },
  {
    title: "BULK SMS",
    description: "Send bulk sms to large number of clients with Codewave SMS Portal.",
    image: bulkSMS,
    color: "#2090B0"
  },
  {
    title: "PAYMENT SOLUTIONS",
    description: "Secure, fast, and reliable payment processing for your business.",
    image: payment,
    color: "#105090"
  },
  {
    title: "SOFTWARE DEVELOPMENT",
    description: "Custom software solutions tailored to your business needs.",
    image: softwarehero,
    color: "#2090B0"
  }
];

const TypedText = ({ services, currentIndex, onTypingComplete }) => {
  const [displayText, setDisplayText] = useState('');
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [phase, setPhase] = useState('typing'); // 'typing', 'waiting', 'deleting', 'complete'

  useEffect(() => {
    // Reset state when currentIndex changes
    setDisplayText('');
    setCharIndex(0);
    setIsDeleting(false);
    setPhase('typing');
  }, [currentIndex]);

  useEffect(() => {
    const currentService = services[currentIndex];
    const text = currentService.title;
    
    let timer;

    switch (phase) {
      case 'typing':
        if (charIndex < text.length) {
          timer = setTimeout(() => {
            setDisplayText(text.substring(0, charIndex + 1));
            setCharIndex(charIndex + 1);
          }, 120);
        } else {
          setPhase('waiting');
        }
        break;

      case 'waiting':
        timer = setTimeout(() => {
          setPhase('deleting');
          setIsDeleting(true);
        }, 2000);
        break;

      case 'deleting':
        if (charIndex > 0) {
          timer = setTimeout(() => {
            setDisplayText(text.substring(0, charIndex - 1));
            setCharIndex(charIndex - 1);
          }, 50);
        } else {
          setPhase('complete');
          setIsDeleting(false);
        }
        break;

      case 'complete':
        timer = setTimeout(() => {
          onTypingComplete();
        }, 500);
        break;

      default:
        break;
    }

    return () => clearTimeout(timer);
  }, [charIndex, phase, currentIndex, services, onTypingComplete]);

  const currentService = services[currentIndex];
  const serviceColor = currentService.color;

  return (
    <TypingText color={serviceColor}>
      {displayText}
    </TypingText>
  );
};

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canAdvance, setCanAdvance] = useState(false);
  const [animationTrigger, setAnimationTrigger] = useState(0);

  // Fetch hero section data from API
  const { data: heroData, loading, error } = useApiData(fetchHeroSection);

  // Only use fallback data when API call is complete (not loading) and no data received
  const services = loading ? [] : (heroData?.services || defaultServices);
  const heading = loading ? "" : (heroData?.heading || "Empowering Businesses with");
  const buttonText = loading ? "" : (heroData?.button_text || "CONTACT US NOW");

  const handleTypingComplete = () => {
    setCanAdvance(true);
  };

  useEffect(() => {
    if (!canAdvance || services.length === 0) return;

    const timer = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length);
      setCanAdvance(false); // Reset for next cycle
      setAnimationTrigger(prev => prev + 1); // Trigger animations
    }, 300); // Small delay before advancing to next service

    return () => clearTimeout(timer);
  }, [canAdvance, services.length]);

  // Show error state if API call failed
  if (error) {
    console.error('Hero section error:', error);
    // Continue with default data instead of showing error
  }

  // Handle empty services array during loading
  const currentService = services.length > 0 ? services[currentIndex] : { color: "#2090B0" };
  
  // Determine if we should use a solid color or gradient based on the service's color
  const useGradient = true;
  
  // Color logic for buttons and elements - ensure we always have valid colors
  const primaryColor = currentService?.color || "#2090B0";
  const secondaryColor = primaryColor === "#2090B0" ? "#105090" : "#2090B0";
  
  // Removed debug logging for better performance

  // Particles initialization
  // const particlesInit = useCallback(async (engine) => {
  //   await loadSlim(engine);
  // }, []);

  const handleScrollDown = () => {
    const nextSection = document.getElementById('home').nextElementSibling;
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({
        top: window.innerHeight,
        behavior: 'smooth'
      });
    }
  };

  // Show full page loader while fetching data
  if (loading) {
    return <FullPageLoader title="Codewave" subtitle="Empowering businesses with technology" />;
  }

  return (
    <HeroSection id="home">
        {/* Particles Background */}
        {/* <ParticlesBackground
          id="tsparticles"
          init={particlesInit}
          options={{
            fullScreen: {
              enable: false,
              zIndex: 0
            },
            fpsLimit: 60,
            particles: {
              number: {
                value: 20,
                density: {
                  enable: true,
                  value_area: 800
                }
              },
              color: {
                value: [
                  "#105090", // Primary color
                  "#2090B0", // Secondary color
                ],
              },
              shape: {
                type: ["circle", "triangle", "polygon"],
                stroke: {
                  width: 0,
                  color: "#000000"
                },
                polygon: {
                  nb_sides: 6
                }
              },
              opacity: {
                value: 0.3,
                random: true,
                anim: {
                  enable: true,
                  speed: 1,
                  opacity_min: 0.1,
                  sync: false
                }
              },
              size: {
                value: 8,
                random: true,
                anim: {
                  enable: true,
                  speed: 10,
                  size_min: 0.1,
                  sync: false
                }
              },
              line_linked: {
                enable: true,
                distance: 150,
                color: "#105090",
                opacity: 0.2,
                width: 1
              },
              move: {
                enable: true,
                speed: 1.5,
                direction: "none",
                random: false,
                straight: false,
                out_mode: "out",
                bounce: false,
                attract: {
                  enable: true,
                  rotateX: 600,
                  rotateY: 1200
                }
              }
            },
            interactivity: {
              detect_on: "canvas",
              events: {
                onhover: {
                  enable: true,
                  mode: "grab"
                },
                onclick: {
                  enable: false,
                  mode: "push"
                },
                resize: true
              },
              modes: {
                grab: {
                  distance: 140,
                  line_linked: {
                    opacity: 0.5
                  }
                }
              }
            },
            retina_detect: true,
            background: {
              color: "#f5f7fa",
              image: "linear-gradient(135deg, #f5f7fa 0%, #e9edf5 100%)",
              position: "50% 50%",
              repeat: "no-repeat",
              size: "cover"
            }
          }}
        /> */}

        {/* Background geometric shapes */}
        <BackgroundShapes>
          <GeometricShape className="circle" color="#105090" top="15%" left="10%" size="100px" />
          <GeometricShape className="square" color="#2090B0" top="65%" left="15%" size="60px" />
          <GeometricShape className="triangle" color="#105090" top="25%" left="85%" size="80px" />
          <GeometricShape className="diamond" color="#2090B0" top="65%" left="80%" size="70px" />
          <GeometricShape className="circle" color="#105090" top="85%" left="40%" size="120px" />
        </BackgroundShapes>

        <HeroContent>
          <TextContainer>
            <HeroHeading>
              {heading}
              <br />
              {services.length > 0 && (
                <TypedText 
                  services={services} 
                  currentIndex={currentIndex} 
                  onTypingComplete={handleTypingComplete}
                />
              )}
            </HeroHeading>
            
            <HeroSubheading
              key={`desc-${animationTrigger}`} // Force remounting for animation
              useGradient={useGradient}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            >
              {services.length > 0 && (
                <StaggeredText text={services[currentIndex].description} />
              )}
            </HeroSubheading>
            
              <HeroButton 
                key={`button-${animationTrigger}-${primaryColor}`}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                useGradient={useGradient}
                onClick={() => {
                  window.location.href = '/contact';
                }}
              >
                {buttonText || "CONTACT US NOW"}
              </HeroButton>
            
            {services.length > 0 && (
              <CarouselDots>
                {services.map((_, index) => (
                  <CarouselDot 
                    key={index} 
                    active={index === currentIndex}
                    serviceColor={services[index].color}
                    onClick={() => {
                      setCurrentIndex(index);
                      setCanAdvance(false); // Reset the advance state
                      setAnimationTrigger(prev => prev + 1); // Trigger animations
                    }}
                  />
                ))}
              </CarouselDots>
            )}
          </TextContainer>
          
          <ImageContainer 
            key={`img-${animationTrigger}`} // Force remounting for animation
            isEven={currentIndex % 2 === 0}
          >
            <ImageFrame primaryColor={primaryColor} secondaryColor={secondaryColor}>
              {services.length > 0 && (
                <ServiceImage 
                  src={services[currentIndex].image_path ? fetchImage(services[currentIndex].image_path) : services[currentIndex].image} 
                  alt={services[currentIndex].title}
                  isEven={currentIndex % 2 === 0}  
                />
              )}
            </ImageFrame>
          </ImageContainer>
        </HeroContent>

        <ScrollDownButton 
          onClick={handleScrollDown}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        >
          <ScrollText>Scroll</ScrollText>
          <ScrollIconContainer>
            <FaChevronDown />
          </ScrollIconContainer>
        </ScrollDownButton>
        
        <BackgroundDecoration primaryColor={primaryColor} />
      </HeroSection>
  );
};

// Helper component for letter-by-letter animation of description text
const StaggeredText = ({ text }) => {
  return (
    <>
      {text.split('').map((char, index) => (
        <AnimatedChar key={index} delay={index * 0.03}>
          {char === ' ' ? '\u00A0' : char}
        </AnimatedChar>
      ))}
    </>
  );
};

// Enhanced Styled Components with new animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  25% {
    transform: translateY(-10px) rotate(1deg);
  }
  50% {
    transform: translateY(0px) rotate(0deg);
  }
  75% {
    transform: translateY(10px) rotate(-1deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.1);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(0, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
`;

const wavyAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const breathe = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.2;
    transform: scale(1.1);
  }
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
`;

const rotateSlowly = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const letterFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseIcon = keyframes`
  0% {
    transform: translateY(0);
    opacity: 0.8;
  }
  50% {
    transform: translateY(5px);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 0.8;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 40px 80px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 992px) {
    padding: 100px 20px 60px;
    flex-direction: column;
  }

  @media (max-width: 576px) {
    padding: 80px 15px 40px;
  }
`;

const ParticlesBackground = styled(Particles)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
`;

const BackgroundDecoration = styled.div`
  position: absolute;
  top: -150px;
  right: -150px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: ${props => `rgba(${props.primaryColor === "#2090B0" ? "32, 144, 176" : "16, 80, 144"}, 0.1)`};
  filter: blur(80px);
  z-index: 1;
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const GeometricShape = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  width: ${props => props.size};
  height: ${props => props.size};
  opacity: 0.08;
  animation: ${breathe} 15s infinite ease-in-out;
  z-index: 0;
  
  &.circle {
    background-color: ${props => props.color};
    border-radius: 50%;
  }
  
  &.square {
    background-color: ${props => props.color};
    transform: rotate(45deg);
    animation: ${breathe} 20s infinite ease-in-out, ${rotateSlowly} 60s infinite linear;
  }
  
  &.triangle {
    background-color: transparent;
    border-left: calc(${props => props.size} / 2) solid transparent;
    border-right: calc(${props => props.size} / 2) solid transparent;
    border-bottom: ${props => props.size} solid ${props => props.color};
    width: 0;
    height: 0;
    animation: ${breathe} 25s infinite ease-in-out;
  }
  
  &.diamond {
    background-color: ${props => props.color};
    transform: rotate(45deg);
    animation: ${breathe} 18s infinite ease-in-out;
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  gap: 40px;
  z-index: 2;
  
  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    gap: 30px;
  }

  @media (max-width: 576px) {
    gap: 20px;
  }
`;

const TextContainer = styled.div`
  flex: 1;
  animation: ${fadeInLeft} 1s ease-out;
  
  @media (max-width: 992px) {
    order: 2;
  }
`;

const ImageContainer = styled.div`
  flex: 1.2;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  animation: ${props => props.isEven ? fadeInRight : fadeInLeft} 0.8s ease-out;
  
  @media (max-width: 992px) {
    order: 1;
    margin-bottom: 20px;
    width: 100%;
    max-width: 450px;
  }

  @media (max-width: 576px) {
    margin-bottom: 10px;
  }
`;

const ImageFrame = styled.div`
  position: relative;
  border-radius: 12px;
  padding: 8px;
  background: linear-gradient(135deg, ${props => props.primaryColor}33, ${props => props.secondaryColor}33);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  
  &:before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, ${props => props.primaryColor}, ${props => props.secondaryColor});
    border-radius: 14px;
    z-index: -1;
    opacity: 0.3;
  }
`;

const ServiceImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  transition: all 0.5s ease;
  animation: ${props => props.isEven ? float : zoomIn} 6s ease-in-out infinite;
  transform-origin: center center;
  object-fit: cover;
  aspect-ratio: 16/9;
  
  @media (min-width: 992px) {
    min-height: 300px;
  }

  &:hover {
    transform: scale(1.03);
  }
`;

const colorFlush = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const HeroHeading = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  
  /* Color flush animation */
  background: linear-gradient(
    90deg,
    #105090 0%,
    #2090B0 25%,
    #105090 50%,
    #2090B0 75%,
    #105090 100%
  );
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: ${colorFlush} 8s ease infinite;
  text-shadow: 0 1px 1px rgba(255, 255, 255, 0.1);
  
  @media (max-width: 992px) {
    font-size: 36px;
  }
  
  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 576px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const TypingText = styled.span`
  position: relative;
  display: inline-block;
  background: none !important;
  background-clip: initial !important;
  -webkit-background-clip: initial !important;
  color: ${props => props.color || "#2090B0"} !important;
  -webkit-text-fill-color: ${props => props.color || "#2090B0"} !important;
  text-shadow: none;
  
  &:after {
    content: '|';
    animation: blink 1s step-end infinite;
  }
  
  @keyframes blink {
    from, to {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

const AnimatedChar = styled.span`
  display: inline-block;
  opacity: 0;
  animation: ${letterFadeIn} 0.6s forwards;
  animation-delay: ${props => props.delay}s;
`;

const HeroSubheading = styled.p`
  font-size: 18px;
  font-weight: 400;
  margin-bottom: 30px;
  line-height: 1.6;
  color: #666666;
  max-width: 600px;
  position: relative;
  padding: 10px 0;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 60px;
    background: ${props => props.useGradient ? 
      `linear-gradient(135deg, ${props.primaryColor} 0%, ${props.secondaryColor} 100%)` : 
      props.primaryColor};
    animation: ${wavyAnimation} 3s ease infinite;
    background-size: 200% 200%;
  }
  
  @media (max-width: 992px) {
    font-size: 16px;
    margin: 0 auto 30px;
    
    &:before {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  @media (max-width: 576px) {
    font-size: 14px;
    margin-bottom: 20px;
    padding: 5px 0;
  }
`;

const HeroButton = styled.button`
  background: ${props => props.useGradient ? 
    `linear-gradient(135deg, ${props.primaryColor} 0%, ${props.secondaryColor} 100%)` : 
    props.primaryColor};
  color: white;
  font-size: 16px;
  font-weight: 500;
  padding: 12px 32px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.4s ease-out 0.2s both;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transition: all 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
    
    &:before {
      left: 100%;
    }
  }
  
  @media (max-width: 768px) {
    padding: 10px 28px;
    font-size: 14px;
  }

  @media (max-width: 576px) {
    padding: 8px 24px;
    font-size: 13px;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 40px;
  justify-content: flex-start;
  
  @media (max-width: 992px) {
    justify-content: center;
    margin-top: 30px;
  }

  @media (max-width: 576px) {
    margin-top: 20px;
    gap: 8px;
  }
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? props.serviceColor : "#E0E0E0"};
  transition: all 0.3s ease;
  cursor: pointer;
  transform: scale(${props => props.active ? 1.2 : 1});
  
  &:hover {
    background-color: ${props => props.active ? props.serviceColor : "#666666"};
    transform: scale(1.1);
  }

  @media (max-width: 576px) {
    width: 10px;
    height: 10px;
  }
`;

const ScrollDownButton = styled.button`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;
  padding: 10px 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateX(-50%) translateY(-5px);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 576px) {
    bottom: 20px;
    padding: 8px 16px;
  }
`;

const ScrollText = styled.span`
  color: #105090;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 5px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const ScrollIconContainer = styled.div`
  color: #2090B0;
  font-size: 18px;
  animation: ${pulseIcon} 1.5s ease-in-out infinite;
`;

export default Hero;
